import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { NgSelectModule } from '@ng-select/ng-select';
import { ICategoriaEvento, IQueryParam, IUbicacion } from 'modelos/src';
import { AuxiliaresModule } from '../../../../../auxiliares/auxiliares.module';
import { ClienteSelectorComponent } from '../../../../../componentes/selectores/cliente-selector/cliente-selector.component';
// DatePickerComponent es standalone y AuxiliaresModule NO lo exporta: sin este
// import el <app-date-picker> no renderizaba nada y el label de Periodo quedaba
// pegado a los campos de prioridad.
import {
  DatePickerComponent,
  DateRangeValue,
} from '../../../../../componentes/date-picker/date-picker.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NgStyle } from '@angular/common';
import { CategoriaEventosService } from '../../../../../auxiliares/servicios/http/categoria-eventos.service';
import { IFiltroAgrupamientoPersonalizado } from '../../agrupamiento-personalizado';

export interface IFiltroPersonalizadoData {
  filtro: IFiltroAgrupamientoPersonalizado;
  /** Zonas ya cargadas por el listado; el dialogo no las vuelve a pedir */
  zonas: IUbicacion[];
}

/**
 * Editor del filtro del agrupamiento personalizado.
 *
 * Todo lo que se elige acá termina en cláusulas Mongo que van al SERVIDOR
 * (`construirFiltroMongo`), no en un filtro sobre el array cargado. Ver
 * PLAN_AGRUPAMIENTO_PERSONALIZADO_EVENTOS.md.
 */
@Component({
  selector: 'app-filtro-personalizado',
  standalone: true,
  imports: [
    AuxiliaresModule,
    FormsModule,
    MatDialogModule,
    NgSelectModule,
    ClienteSelectorComponent,
    DatePickerComponent,
    DragDropModule,
    NgStyle,
  ],
  templateUrl: './filtro-personalizado.component.html',
  styleUrls: ['./filtro-personalizado.component.scss'],
})
export class FiltroPersonalizadoComponent {
  private data = inject<IFiltroPersonalizadoData>(MAT_DIALOG_DATA);
  private dialogRef =
    inject<MatDialogRef<FiltroPersonalizadoComponent>>(MatDialogRef);
  private categoriasService = inject(CategoriaEventosService);

  /** Copia: si el operador cancela, el filtro del listado no se toca */
  public filtro: IFiltroAgrupamientoPersonalizado = {
    ...(this.data?.filtro ?? {}),
  };
  public zonas: IUbicacion[] = this.data?.zonas ?? [];

  /**
   * Categorías de evento para el filtro, deduplicadas por nombre.
   *
   * El evento guarda `categoria` como NOMBRE (no como id), así que el filtro
   * matchea por nombre. En prod hay 25 categorías globales y 10 de cliente, con
   * nombres que se repiten entre las dos (BUR, Sin Reportar, Posición): sin
   * deduplicar, la lista mostraría "BUR" dos veces y las dos opciones armarían
   * exactamente el mismo filtro.
   */
  public categorias: ICategoriaEvento[] = [];
  public cargandoCategorias = true;

  public readonly tiposEvento = [
    'Evento Alarma',
    'Evento Vehiculo',
    'Evento Luminaria',
    'Evento Activo',
    'Evento Tracker',
    'Evento Colectivo',
    'Evento Gateway',
    'Evento BotonBLE',
    'Evento Sirena',
  ].map((valor) => ({
    valor,
    label: valor === 'Evento Vehiculo' ? 'Evento Vehículo' : valor,
  }));

  public readonly estados = [
    'Sin Tratamiento',
    'Pendiente',
    'En Atención',
    'En Espera',
    'Liberada',
  ];

  constructor() {
    this.listarCategorias();
  }

  private async listarCategorias(): Promise<void> {
    // Las globales ya vienen: `HelperService.agregarFiltro` del backend mete
    // siempre `{global: true}` en su $or, no hace falta pedirlas aparte.
    const query: IQueryParam = {
      select: 'nombre global',
      sort: 'nombre',
      unlimited: true,
      includeChildren: true,
    };
    try {
      const res = await this.categoriasService.getFiltered(query);
      this.categorias = this.dedupePorNombre(res.datos ?? []);
    } catch (error) {
      console.error('Error cargando categorías de evento', error);
    } finally {
      this.cargandoCategorias = false;
    }
  }

  /**
   * Una entrada por nombre. El globo se muestra sólo si TODAS las categorías con
   * ese nombre son globales: si el nombre existe además como categoría propia de
   * un cliente, marcarlo como global sería mentir.
   */
  private dedupePorNombre(categorias: ICategoriaEvento[]): ICategoriaEvento[] {
    const porNombre = new Map<string, ICategoriaEvento[]>();
    for (const c of categorias) {
      if (!c.nombre) continue;
      const previas = porNombre.get(c.nombre);
      if (previas) previas.push(c);
      else porNombre.set(c.nombre, [c]);
    }
    return [...porNombre.entries()]
      .map(([nombre, lista]) => ({
        ...lista[0],
        nombre,
        global: lista.every((c) => c.global),
      }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }

  /**
   * Rango de fechas del `app-date-picker`. Campo PLANO, no un par
   * getter/setter: con `[(ngModel)]` Angular compara el valor del modelo por
   * referencia en cada ciclo de deteccion, y un getter que arma `{start, end}`
   * nuevo en cada lectura se ve siempre distinto -> el CVA reescribe, eso
   * dispara otro ciclo y la pantalla se cuelga. Mismo patron que
   * `dato.rangeValue` en auxiliares/tabla/filtro.
   */
  public rangoFechas: DateRangeValue | null = this.rangoDesdeFiltro();

  private rangoDesdeFiltro(): DateRangeValue | null {
    const start = this.data?.filtro?.fechaDesde ?? '';
    const end = this.data?.filtro?.fechaHasta ?? '';
    return start || end ? { start, end } : null;
  }

  public onRangoFechasChange(valor: DateRangeValue | null): void {
    this.filtro.fechaDesde = valor?.start || undefined;
    this.filtro.fechaHasta = valor?.end || undefined;
  }

  public limpiar(): void {
    this.filtro = {};
    this.rangoFechas = null;
  }

  public aplicar(): void {
    // Se normaliza acá y no en construirFiltroMongo: un array vacío guardado en
    // localStorage se leería como filtro activo y mostraría chips de más.
    const limpio: Record<string, unknown> = {};
    for (const [clave, valor] of Object.entries(this.filtro)) {
      if (Array.isArray(valor)) {
        if (valor.length) limpio[clave] = valor;
      } else if (valor !== undefined && valor !== null && valor !== '') {
        limpio[clave] = valor;
      }
    }
    this.dialogRef.close(limpio as IFiltroAgrupamientoPersonalizado);
  }

  public cancelar(): void {
    this.dialogRef.close();
  }
}
